import React, { useState } from "react";
import { FinancialAccount } from "@common/types/financialAccount";
import { useFinancialAccountUpdateMutation } from "@api/financialAccount.api";
import { useIdCopy } from "@hooks/useIdCopy";
import { Field } from "@design-system/Field/Field";
import { Input } from "@design-system/Input/Input";

interface AccountNameProps {
    account: FinancialAccount;
}

export const AccountName: React.FC<AccountNameProps> = ({ account }) => {
    const [editedValue, setEditedValue] = useState<string>(account.name ?? "");
    const updateAccount = useFinancialAccountUpdateMutation(account, {
        optimisticUpdate: true,
    });
    const { getCopyProps } = useIdCopy({ label: "Financial Account" });

    const save = () => {
        const nameToSave = editedValue.trim();
        if (!nameToSave || nameToSave === account.name) {
            return;
        }

        updateAccount.mutate(
            { name: nameToSave },
            {
                // A refused save (e.g. a view-only 403) rolls the cache back,
                // but this input holds its own state — snap it back too so the
                // field doesn't keep the rejected text.
                onError: () => setEditedValue(account.name ?? ""),
            },
        );
    };

    const copyProps = getCopyProps(account.id);
    const scopedCopyProps = copyProps.onContextMenu
        ? {
              ...copyProps,
              onContextMenu: (e: React.MouseEvent) => {
                  if (e.target === e.currentTarget) {
                      copyProps.onContextMenu?.(e);
                  }
              },
          }
        : copyProps;

    return (
        <div {...scopedCopyProps} className="account-details__field__control">
            <Field.Root>
                <Input
                    type="text"
                    size="lg"
                    value={editedValue}
                    onChange={(event) => setEditedValue(event.target.value)}
                    onBlur={save}
                />
            </Field.Root>
        </div>
    );
};
